import React from 'react'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'
import Icon, { IconName } from 'particles/Icon'
import { SemanticColors } from 'particles/colors'

type IndicatorType = 'approve' | 'reject' | 'edit'
export type IndicatorColor = keyof SemanticColors['indicator']
type SizeType = 's' | 'm' | 'l'

interface ProfileProps {
    uuid: string
    name: string
    photo?: string
}

interface AvatarContainerProps {
    size: SizeType
    indicatorColor?: IndicatorColor
    profileBgColor: IndicatorColor
}

interface AvatarProps {
    profile: ProfileProps
    size?: SizeType
    form?: IndicatorType
    imageToIgnore?: string // Ignore this image if provided and display character instead
    className?: string
    onMouseEnter?: () => void
    onMouseLeave?: () => void
}

const sizeMap = {
    s: '24px',
    m: '28px',
    l: '40px',
}

const AvatarContainer = styled.div<AvatarContainerProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${({ size }) => sizeMap[size]};
    height: ${({ size }) => sizeMap[size]};
    min-width: ${({ size }) => sizeMap[size]};
    max-height: ${({ size }) => sizeMap[size]};
    border-radius: 50%;
    position: relative;
    background-color: ${({ theme, profileBgColor }) => theme.colors.indicator[profileBgColor ?? 'lightGrey']};

    .indicator {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 12px;
        height: 12px;
        position: absolute;
        bottom: 0;
        right: -4px;
        border: ${({ theme }) => `1px solid ${theme.colors.border.tertiary}`};
        border-radius: 50%;
        box-sizing: border-box;
        background-color: ${({ theme, indicatorColor }) => theme.colors.indicator[indicatorColor ?? 'darkOrange']};
    }

    .profile-img {
        width: 100%;
        height: inherit;
        border-radius: 50%;
        align-self: center;
        object-fit: cover;
        border: ${({ theme, size }) => `${size === 's' ? '0.5px' : '1px'} solid ${theme.colors.border.primary}`};
        box-sizing: border-box;
    }
`

// Characters in thai words that are not considered 'letters'.
// These characters are to be removed from the name string so that only real letters are used for the profile display.
const charsToSanitize = ['เ', 'แ', 'โ', 'ไ', 'ใ']

const profileBgColorArr = ['darkBlue', 'black', 'yolkYellow', 'darkGreen', 'lightBlue'] as const

const indicatorMap = (indicator: IndicatorType): { name: IconName; color: IndicatorColor } => {
    const map: Record<IndicatorType, { name: IconName; color: IndicatorColor }> = {
        approve: { name: 'check', color: 'doneGreen' },
        reject: { name: 'x', color: 'ribbonRed' },
        edit: { name: 'pencilSimple', color: 'darkOrange' },
    }
    return map[indicator]
}

const Avatar: React.FC<AvatarProps> = ({
    profile,
    form,
    size = 's',
    imageToIgnore,
    className,
    onMouseEnter,
    onMouseLeave,
}) => {
    const sanitizedName = String(profile?.name).replace(new RegExp(`[${charsToSanitize.join('')}]`, 'g'), '')
    const profileImgChar = sanitizedName?.[0]?.toUpperCase() ?? '?'
    const profileBgColor = profileBgColorArr[profile?.uuid.charCodeAt(profile?.uuid.length - 1) % 5]
    const indicatorValues = form && indicatorMap(form)
    const profileImg = profile?.photo === imageToIgnore ? null : profile?.photo

    return (
        <AvatarContainer
            size={size}
            className={className}
            profileBgColor={profileBgColor}
            indicatorColor={indicatorValues?.color}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {profileImg && typeof profileImg === 'string' ? (
                <img className="profile-img" src={profileImg} />
            ) : (
                <Text scale="bodyM500" color="primaryInverse" textAlign="center" style={{ paddingBottom: 2 }}>
                    {profileImgChar}
                </Text>
            )}
            {form && indicatorValues ? (
                <div className="indicator">
                    <Icon name={indicatorValues?.name} size="8px" color="primaryInverse" />
                </div>
            ) : null}
        </AvatarContainer>
    )
}

export default Avatar
